import * as THREE from 'three';
import { PointCloudSM } from '../utils/PointCloudSM';
import { EyeDomeLightingMaterial } from '../materials/EyeDomeLightingMaterial';
import { Utils } from '../utils';
import { enableDepthFadeOnLineMaterial } from '../materials/DepthFadeUtils';
import type { RenderPipeline, RenderPipelineContext, RenderPipelineState } from './RenderPipeline';

/** Material with optional depth fade uniforms and setup flag */
interface DepthFadeMaterial extends THREE.Material {
  uniforms?: Record<string, { value: unknown }>;
  _depthFadeSetup?: boolean;
}

/** Measurement-like object with optional visual elements */
interface MeasureLike {
  depthFadeEnabled?: boolean;
  lineMaterial?: DepthFadeMaterial;
  backgroundLineMaterial?: DepthFadeMaterial;
  backgroundLineTouchMaterial?: DepthFadeMaterial;
  spheres?: Array<{ mesh?: { material?: THREE.Material | THREE.Material[] }; backgroundMesh?: { material?: THREE.Material | THREE.Material[] } }>;
  edgeLabels?: Array<{ material?: DepthFadeMaterial }>;
  coordinateLabels?: Array<{ material?: DepthFadeMaterial }>;
  areaLabel?: { material?: DepthFadeMaterial };
  volumeLabel?: { material?: DepthFadeMaterial };
  volumeLabels?: Array<{ material?: DepthFadeMaterial }>;
}

/** Spot light with optional shadow update flag */
interface SpotLightLike extends THREE.Object3D {
  type: string;
  disableShadowUpdates?: boolean;
}

/**
 * Per-view state for the EDL pipeline. Each RenderView gets its own RTs and
 * shadow map; the EDL post-process material is also per-view because its
 * uniforms (resolution, near/far) are camera-dependent.
 */
export interface EDLPipelineState extends RenderPipelineState {
  readonly kind: 'edl';
  edlMaterial: EyeDomeLightingMaterial;
  rtEDL: THREE.WebGLRenderTarget;
  rtRegular: THREE.WebGLRenderTarget;
  shadowMap: PointCloudSM;
}

export class EDLRenderer implements RenderPipeline<EDLPipelineState> {
  readonly kind = 'edl' as const;
  measurementDepthFadeEnabled: boolean;

  /** Lazy renderer access — the viewer's renderer may not exist at construction. */
  private readonly getRenderer: (() => THREE.WebGLRenderer | null) | null;

  constructor(getRenderer?: () => THREE.WebGLRenderer | null) {
    // Global toggle for shader-based depth fade on measurements (default: enabled)
    this.measurementDepthFadeEnabled = true;
    this.getRenderer = getRenderer ?? null;
  }

  /**
   * The EDL color target stores log2 depth in its alpha channel — it MUST
   * keep float precision or the edge response bands into visible stripes.
   * Float render targets are an optional capability: fall back to half-float
   * (and warn) rather than letting the driver silently degrade the buffer.
   */
  private edlTargetType(): THREE.TextureDataType {
    const renderer = this.getRenderer?.() ?? null;
    if (!renderer) return THREE.FloatType;
    const gl = renderer.getContext();
    if (gl.getExtension('EXT_color_buffer_float')) return THREE.FloatType;
    if (gl.getExtension('EXT_color_buffer_half_float')) {
      console.warn('[EDLRenderer] Float render targets unsupported — falling back to half-float (EDL depth precision reduced)');
      return THREE.HalfFloatType;
    }
    console.warn('[EDLRenderer] Float/half-float render targets unsupported — EDL quality will be degraded');
    return THREE.UnsignedByteType;
  }

  createState(): EDLPipelineState {
    const edlMaterial = new EyeDomeLightingMaterial();
    edlMaterial.depthTest = true;
    edlMaterial.depthWrite = true;
    edlMaterial.transparent = true;

    const rtEDL = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: this.edlTargetType(),
      depthTexture: new THREE.DepthTexture(undefined as unknown as number, undefined as unknown as number, THREE.UnsignedIntType),
    });

    const rtRegular = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      depthTexture: new THREE.DepthTexture(undefined as unknown as number, undefined as unknown as number, THREE.UnsignedIntType),
    });

    // shadowMap holds a reference to the low-level Potree renderer; we'll
    // populate that in render() since the pRenderer comes from ctx.
    // Using a lazily-bound shadow map is safe: it's only used when a
    // SpotLight is in the scene.
    return {
      kind: 'edl',
      edlMaterial,
      rtEDL,
      rtRegular,
      shadowMap: null as unknown as PointCloudSM,
    };
  }

  disposeState(state: EDLPipelineState): void {
    state.edlMaterial.dispose();
    state.rtEDL.dispose();
    state.rtRegular.dispose();
  }

  clear(ctx: RenderPipelineContext, state: EDLPipelineState): void {
    const { threeRenderer } = ctx;
    threeRenderer.setClearColor(0x000000, 0);
    threeRenderer.clear();

    const oldTarget = threeRenderer.getRenderTarget();
    threeRenderer.setRenderTarget(state.rtEDL);
    threeRenderer.clear(true, true, true);
    threeRenderer.setRenderTarget(state.rtRegular);
    threeRenderer.clear(true, true, false);
    threeRenderer.setRenderTarget(oldTarget);
  }

  private renderShadowMap(ctx: RenderPipelineContext, state: EDLPipelineState, lights: SpotLightLike[]): void {
    const { viewer, camera } = ctx;
    const doShadows = lights.length > 0 && !lights[0].disableShadowUpdates;
    if (!doShadows) return;

    // Lazily bind shadowMap to ctx.pRenderer the first time we need it.
    if (state.shadowMap === null) {
      state.shadowMap = new PointCloudSM(ctx.pRenderer);
    }

    const light = lights[0];
    state.shadowMap.setLight(light as unknown as THREE.SpotLight);

    // Snapshot the active attribute so we can restore after rendering depth.
    const originalAttributes = new Map<(typeof viewer.scene.pointclouds)[number], string | null>();
    for (const pointcloud of viewer.scene.pointclouds) {
      originalAttributes.set(pointcloud, pointcloud.material.activeAttributeName);
      pointcloud.material.disableEvents();
      pointcloud.material.activeAttributeName = 'depth';
    }

    state.shadowMap.render(viewer.scene.scenePointCloud, camera);

    for (const pointcloud of viewer.scene.pointclouds) {
      const originalAttribute = originalAttributes.get(pointcloud);
      if (originalAttribute !== undefined) {
        pointcloud.material.activeAttributeName = originalAttribute;
      }
      pointcloud.material.enableEvents();
    }

    viewer.shadowTestCam.updateMatrixWorld();
    viewer.shadowTestCam.matrixWorldInverse.copy(viewer.shadowTestCam.matrixWorld).invert();
    viewer.shadowTestCam.updateProjectionMatrix();
  }

  render(ctx: RenderPipelineContext, state: EDLPipelineState): void {
    const { viewer, threeRenderer, pRenderer, camera, width, height, clipSpheres, overlays } = ctx;

    viewer.dispatchEvent({ type: 'render.pass.begin', viewer });

    state.rtEDL.setSize(width, height);
    state.rtRegular.setSize(width, height);

    const visiblePointClouds = viewer.scene.pointclouds.filter((pc) => pc.visible);

    // Find spot lights for shadow rendering.
    const lights: SpotLightLike[] = [];
    viewer.scene.scene.traverse((node: THREE.Object3D) => {
      if (node.type === 'SpotLight') {
        lights.push(node as SpotLightLike);
      }
    });

    this.renderShadowMap(ctx, state, lights);

    // COLOR & DEPTH PASS — set up materials for EDL.
    for (const pointcloud of visiblePointClouds) {
      const octreeSize = pointcloud.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
      const material = pointcloud.material;
      material.weighted = false;
      material.useLogarithmicDepthBuffer = false;
      material.useEDL = true;
      material.screenWidth = width;
      material.screenHeight = height;
      material.uniforms.visibleNodes.value = pointcloud.material.visibleNodesTexture;
      material.uniforms.octreeSize.value = octreeSize;
      material.spacing = pointcloud.pcoGeometry.spacing;
    }

    threeRenderer.setRenderTarget(state.rtEDL);

    const renderParams: Record<string, unknown> = {
      clipSpheres,
      transparent: false,
      orderedIdentifiers: viewer.scene.orderedIdentifiers || [],
    };
    if (lights.length > 0) renderParams.shadowMaps = [state.shadowMap];
    pRenderer.render(viewer.scene.scenePointCloud, camera, state.rtEDL, renderParams);

    viewer.dispatchEvent({ type: 'render.pass.scene', viewer, renderTarget: state.rtRegular });
    threeRenderer.setRenderTarget(null);
    threeRenderer.render(viewer.scene.scene, camera);

    {
      // EDL composite pass
      const uniforms = state.edlMaterial.uniforms;
      uniforms.screenWidth.value = width;
      uniforms.screenHeight.value = height;

      const proj = (camera as THREE.PerspectiveCamera).projectionMatrix;
      const projArray = new Float32Array(16);
      projArray.set(proj.elements);

      uniforms.uNear.value = (camera as THREE.PerspectiveCamera).near;
      uniforms.uFar.value = (camera as THREE.PerspectiveCamera).far;
      uniforms.uEDLColor.value = state.rtEDL.texture;
      uniforms.uEDLDepth.value = state.rtEDL.depthTexture;
      uniforms.uProj.value = projArray;
      uniforms.edlStrength.value = viewer.edlStrength;
      uniforms.radius.value = viewer.edlRadius;
      uniforms.opacity.value = viewer.edlOpacity;

      Utils.screenPass.render(threeRenderer, state.edlMaterial);
    }

    // Snapshot the framebuffer for the wall-skin lines' invert shader NOW —
    // straight after the point cloud composite, before ANY overlay scene
    // renders — so the capture can never contain the lines themselves (a
    // later capture risks feeding the lines their own previous output,
    // which oscillates into noise).
    if (overlays.floorplan && viewer.scene.wallSketches.length > 0) {
      viewer.backdropCapture.capture(threeRenderer);
    }

    if (overlays.volumes) threeRenderer.render(viewer.volumeScene, camera);

    this.updateMeasurementMaterials(ctx, state);

    if (!this.measurementDepthFadeEnabled) {
      threeRenderer.clearDepth();
    }

    if (overlays.measurements) threeRenderer.render(viewer.measurementScene, camera);
    if (overlays.planes) threeRenderer.render(viewer.planeScene, camera);

    if (overlays.controls && viewer.controls.sceneControls) threeRenderer.render(viewer.controls.sceneControls, camera);
    if (overlays.clippingVolumeMesh) threeRenderer.render(viewer.clippingTool.sceneVolume, camera);
    if (overlays.floorplan) threeRenderer.render(viewer.floorplanTool.scene, camera);
    if (overlays.toolOverlay) {
      for (const toolScene of viewer.toolOverlayScenes) threeRenderer.render(toolScene, camera);
    }

    // Top-most UI overlay: clear depth first so its contents always draw over
    // every other scene (point cloud, floorplan/skeleton meshes, etc.).
    if (viewer.uiScene) {
      threeRenderer.clearDepth();
      threeRenderer.render(viewer.uiScene, camera);
    }

    // Transform gizmos render LAST — the manipulation widget must never be
    // buried under glyphs/overlays (its handles sit ON selected objects).
    if (overlays.gizmo && viewer.gizmoScene) {
      threeRenderer.clearDepth();
      threeRenderer.render(viewer.gizmoScene, camera);
    }

    viewer.dispatchEvent({ type: 'render.pass.end', viewer });
  }

  /**
   * Update measurement materials to enable/disable depth fading.
   * Per-view because the depth texture is per-view (lives on state.rtEDL).
   */
  updateMeasurementMaterials(ctx: RenderPipelineContext, state: EDLPipelineState): void {
    const { viewer } = ctx;
    const measurements = viewer.scene.measurements || [];

    for (const measure of measurements) {
      this.updateMeasureMaterials(ctx, state, measure);
    }
    const rooms = viewer.scene.rooms || [];
    for (const room of rooms) {
      this.updateMeasureMaterials(ctx, state, room);
    }
    const wallSketches = viewer.scene.wallSketches || [];
    for (const skeleton of wallSketches) {
      this.updateMeasureMaterials(ctx, state, skeleton);
    }

    // Outlines: outer ring goes through the standard MeasureLike path;
    // hole materials need the same depth-fade setup wired separately.
    const outlines = viewer.scene.outlines || [];
    if (outlines.length > 0) {
      const depthTexture = state.rtEDL.depthTexture;
      const near = (ctx.camera as THREE.PerspectiveCamera).near ?? 0.1;
      const far = (ctx.camera as THREE.PerspectiveCamera).far ?? 10000;
      for (const outline of outlines) {
        this.updateMeasureMaterials(ctx, state, outline);
        const enabled = this.measurementDepthFadeEnabled && (outline.depthFadeEnabled ?? true);
        for (const mat of outline.holeLineMaterials) {
          const shaderMat = mat as DepthFadeMaterial;
          if (!shaderMat._depthFadeSetup) {
            enableDepthFadeOnLineMaterial(mat);
          }
          if (shaderMat.uniforms?.uDepthTexture) {
            shaderMat.uniforms.uDepthTexture.value = depthTexture;
            shaderMat.uniforms.uNear.value = near;
            shaderMat.uniforms.uFar.value = far;
            shaderMat.uniforms.uDepthFadeEnabled.value = enabled;
            (shaderMat.uniforms.uResolution.value as THREE.Vector2).set(ctx.width, ctx.height);
          }
          mat.depthTest = false;
          mat.polygonOffset = false;
        }
      }
    }

    const planes = viewer.scene.planes || [];
    if (planes.length > 0) {
      const depthTexture = state.rtEDL.depthTexture;
      const near = (ctx.camera as THREE.PerspectiveCamera).near ?? 0.1;
      const far = (ctx.camera as THREE.PerspectiveCamera).far ?? 10000;
      for (const plane of planes) {
        const enabled = this.measurementDepthFadeEnabled && (plane.depthFadeEnabled ?? true);
        const materials = Array.isArray(plane.circle.material) ? plane.circle.material : [plane.circle.material];
        for (const mat of materials) {
          const shaderMat = mat as DepthFadeMaterial;
          if (shaderMat.uniforms?.uDepthTexture) {
            shaderMat.uniforms.uDepthTexture.value = depthTexture;
            shaderMat.uniforms.uNear.value = near;
            shaderMat.uniforms.uFar.value = far;
            shaderMat.uniforms.uDepthFadeEnabled.value = enabled;
            (shaderMat.uniforms.uResolution.value as THREE.Vector2).set(ctx.width, ctx.height);
          }
          mat.depthTest = false;
          mat.polygonOffset = false;
        }
      }
    }
  }

  updateMeasureMaterials(ctx: RenderPipelineContext, state: EDLPipelineState, measure: MeasureLike): void {
    const enabled = this.measurementDepthFadeEnabled && (measure.depthFadeEnabled ?? true);
    const depthTexture = state.rtEDL.depthTexture;
    const near = (ctx.camera as THREE.PerspectiveCamera).near ?? 0.1;
    const far = (ctx.camera as THREE.PerspectiveCamera).far ?? 10000;

    const setupLineMaterial = (material: DepthFadeMaterial | undefined) => {
      if (!material) return;
      if (!material._depthFadeSetup) {
        enableDepthFadeOnLineMaterial(material as unknown as import('three/examples/jsm/lines/LineMaterial.js').LineMaterial);
      }
      if (material.uniforms?.uDepthTexture) {
        material.uniforms.uDepthTexture.value = depthTexture;
        material.uniforms.uNear.value = near;
        material.uniforms.uFar.value = far;
        material.uniforms.uDepthFadeEnabled.value = enabled;
        (material.uniforms.uResolution.value as THREE.Vector2).set(ctx.width, ctx.height);
      }
      material.depthTest = false;
      material.polygonOffset = false;
    };

    setupLineMaterial(measure.lineMaterial);
    setupLineMaterial(measure.backgroundLineMaterial);
    setupLineMaterial(measure.backgroundLineTouchMaterial);

    const configureDepthFadeMaterial = (material: THREE.Material | THREE.Material[] | undefined) => {
      if (!material) return;
      if (Array.isArray(material)) {
        material.forEach((m) => configureDepthFadeMaterial(m));
        return;
      }
      const mat = material as DepthFadeMaterial;
      if (!mat.uniforms) return;
      if (mat.uniforms.uDepthTexture) {
        mat.uniforms.uDepthTexture.value = depthTexture;
        mat.uniforms.uNear.value = near;
        mat.uniforms.uFar.value = far;
        mat.uniforms.uDepthFadeEnabled.value = enabled;
        (mat.uniforms.uResolution.value as THREE.Vector2).set(ctx.width, ctx.height);
      }
      material.depthTest = false;
      material.polygonOffset = false;
    };

    if (measure.spheres) {
      for (const sphere of measure.spheres) {
        configureDepthFadeMaterial(sphere.mesh?.material);
        configureDepthFadeMaterial(sphere.backgroundMesh?.material);
      }
    }
    if (measure.edgeLabels) {
      for (const label of measure.edgeLabels) {
        configureDepthFadeMaterial(label.material);
      }
    }
    if (measure.coordinateLabels) {
      for (const label of measure.coordinateLabels) {
        configureDepthFadeMaterial(label.material);
      }
    }
    if (measure.areaLabel) {
      configureDepthFadeMaterial(measure.areaLabel.material);
    }
    if (measure.volumeLabel) {
      configureDepthFadeMaterial(measure.volumeLabel.material);
    }
    if (measure.volumeLabels) {
      for (const label of measure.volumeLabels) {
        configureDepthFadeMaterial(label.material);
      }
    }
  }

  setMeasurementDepthFadeEnabled(enabled: boolean): void {
    this.measurementDepthFadeEnabled = enabled;
  }
}
